import { useAtom, useSetAtom } from 'jotai';
import { ActionableNotification } from '@carbon/react';
import { SingleSelectForm } from '../../single select form/single-select-form';
import {
  fittedProductionAtom,
  recordVersionsAtom,
  selectedProductionSourceAtom,
  selectedRecordVersionAtom,
  SelectedRecordVersion,
} from '../atoms';
import { currentOrFirstVersion } from '../utils';
import { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { WellVersionsResponse } from 'types/api-responses';
import {
  IHS_ONLY_SOURCES,
  isDailyProductionSource,
  ProductionSourceDropdown,
} from 'components/production-source-dropdown';
import type {
  ProductionDataSource,
  WellProductionDataSources,
} from 'types/api-responses';
import style from './production-version-selector.module.scss';
import { parseMomentUtc, US_DATE_FORMAT } from 'utils/dates';

interface ProductionVersionSelectorProps {
  /** IHS production versions. */
  recordVersionHref: string;
  /** Daily production versions; without it only IHS is offered. */
  dailyRecordVersionHref?: string;
  /** The sources the well has production for and its default, or undefined while they load. Used only with dailyRecordVersionHref. */
  productionDataSources?: WellProductionDataSources;
  uwi: string;
}

/**
 * Picks the production the DCA editor reads: the source, when the well has
 * daily production to offer, and the version within it. The well starts on its
 * default source, the one the DCA scenario's hierarchy picks, and each source
 * starts on its current version.
 *
 * @param {ProductionVersionSelectorProps} props - The well, its versions endpoints and the sources it has.
 * @return {JSX.Element} The source dropdown and the version picker.
 */
export const ProductionVersionSelector = ({
  recordVersionHref,
  dailyRecordVersionHref,
  productionDataSources,
  uwi,
}: ProductionVersionSelectorProps) => {
  const [recordVersions, fetchRecordVersions] = useAtom(recordVersionsAtom);
  const [selectedRecordVersion, setSelectedRecordVersion] = useAtom(
    selectedRecordVersionAtom
  );
  const [productionSource, setProductionSource] = useAtom(
    selectedProductionSourceAtom
  );
  const sources = dailyRecordVersionHref
    ? productionDataSources
    : IHS_ONLY_SOURCES;
  // The number of the last versions request. A response to an earlier one is
  // stale: a slow list must not land after the well or source moved on, or
  // after a newer request for the same source answered first.
  const lastRequestRef = useRef(0);
  // The last versions accepted, with the well and source they belong to.
  const [answer, setAnswer] = useState<{
    uwi: string;
    source: ProductionDataSource;
    versions: WellVersionsResponse[];
  } | null>(null);
  // The well and source whose versions last failed to load. Without a version
  // Fit and Save stay off, so the failure is shown with a retry.
  const [failed, setFailed] = useState<{
    uwi: string;
    source: ProductionDataSource;
  } | null>(null);

  // Lists a source's versions and selects its current one.
  const loadVersions = useCallback(
    (source: ProductionDataSource) => {
      lastRequestRef.current += 1;
      const request = lastRequestRef.current;
      fetchRecordVersions({
        url:
          isDailyProductionSource(source) && dailyRecordVersionHref
            ? dailyRecordVersionHref
            : recordVersionHref,
        type: 'POST',
        data: {
          query: {
            uwis: [uwi],
          },
        },
        onSuccess: (data) => {
          if (request !== lastRequestRef.current) return;
          setFailed(null);
          setAnswer({ uwi, source, versions: data.results ?? [] });
          const version = currentOrFirstVersion(data.results ?? []);
          if (version) {
            const currentVersion = {
              ...version,
              id: version.versionId,
              name: version.versionName,
            };
            setSelectedRecordVersion(currentVersion);
          }
        },
        onError: () => {
          if (request !== lastRequestRef.current) return;
          setFailed({ uwi, source });
        },
      });
    },
    [
      uwi,
      recordVersionHref,
      dailyRecordVersionHref,
      fetchRecordVersions,
      setSelectedRecordVersion,
    ]
  );

  const setFittedProduction = useSetAtom(fittedProductionAtom);

  // A new well starts with no version, so nothing is read or fitted with the
  // previous well's version and source while its own sources load, and with no
  // fit, so nothing is saved with the previous well's fitted production. The
  // previous well's versions request, if still out, is stale.
  useEffect(() => {
    lastRequestRef.current += 1;
    setSelectedRecordVersion(null);
    setProductionSource('ihs');
    setFittedProduction(null);
  }, [uwi, setSelectedRecordVersion, setProductionSource, setFittedProduction]);

  // Once the well's sources are known, start on its default source, with no
  // version until that source's versions arrive, as a source change does.
  useEffect(() => {
    if (!sources) return;
    setSelectedRecordVersion(null);
    setProductionSource(sources.defaultSource);
    loadVersions(sources.defaultSource);
  }, [sources, loadVersions, setProductionSource, setSelectedRecordVersion]);

  // The selected source's versions for this well, once they arrive.
  const listedVersions = useMemo(
    () =>
      answer?.uwi === uwi && answer.source === productionSource
        ? answer.versions
        : [],
    [answer, uwi, productionSource]
  );

  const options: SelectedRecordVersion[] = useMemo(
    () =>
      listedVersions.map(
        (version: WellVersionsResponse): SelectedRecordVersion => ({
          ...version,
          id: version.versionId,
          name: version.versionName,
        })
      ),
    [listedVersions]
  );

  const handleChange = useCallback(
    (version: SelectedRecordVersion | null) => {
      if (!version) return;
      setSelectedRecordVersion(version);
    },
    [setSelectedRecordVersion]
  );

  // Each source has its own versions, so switching starts on the new source's
  // current version.
  const handleSourceChange = useCallback(
    (source: ProductionDataSource) => {
      setSelectedRecordVersion(null);
      setProductionSource(source);
      loadVersions(source);
    },
    [loadVersions, setProductionSource, setSelectedRecordVersion]
  );

  return (
    <>
      {dailyRecordVersionHref && (
        <ProductionSourceDropdown
          className={style.sourceDropdown}
          value={productionSource}
          availableSources={sources?.availableSources ?? []}
          onChange={handleSourceChange}
        />
      )}
      <SingleSelectForm
        id="production-version-selector"
        options={options}
        value={selectedRecordVersion}
        onChange={handleChange}
        showNoneOption={false}
        isLoading={recordVersions.loading || !sources}
        onSearch={(text, option) => {
          const parsedVersionName = option.versionName.replace(
            /^Version v(\d+)$/i,
            'Version $1'
          );
          return parsedVersionName.toLowerCase().includes(text.toLowerCase());
        }}
        renderLabel={(version) => <RecordVersionLabel version={version} />}
      />
      {/* Stays up while a retry runs, so focus stays on its button. */}
      {failed?.uwi === uwi && failed.source === productionSource && (
        <ActionableNotification
          inline
          kind="error"
          lowContrast
          hideCloseButton
          hasFocus={false}
          title="Couldn't load the production versions"
          actionButtonLabel={recordVersions.loading ? 'Retrying...' : 'Retry'}
          onActionButtonClick={() => {
            if (!recordVersions.loading) loadVersions(productionSource);
          }}
        />
      )}
    </>
  );
};

const RecordVersionLabel = ({
  version,
}: {
  version: SelectedRecordVersion;
}) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return parseMomentUtc(dateString).format(US_DATE_FORMAT);
  };

  const createdDate = formatDate(version.beginTime);
  const discardedDate = formatDate(version.endTime);

  return (
    <div className={style.versionLabel}>
      <div className={style.versionName}>
        {version.versionName.replace(/^Version v(\d+)$/i, 'Version $1')}
      </div>
      <div className={style.versionDetails}>
        {createdDate && `Created: ${createdDate}`}
        {discardedDate && ` | Discarded: ${discardedDate}`}
      </div>
    </div>
  );
};
